import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import search from '~/redux/slices/search';
import { MODAL_DELETE_SAVED_SEARCH } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';

const DeleteSavedSearchModal = () => {
  const intl = useIntl();
  const modal = useSelector(selectors.selectSearchResultModal);
  const dispatch = useDispatch();
  const hideModal = () => dispatch(search.actions.setModal());
  const { modalType, context } = modal || {};

  const [deleteSavedSearch, deleteSavedSearchResult] =
    api.endpoints.deleteSavedSearch.useMutation();
  const {
    isError: deleteSavedSearchError,
    isLoading: isDeletingSavedSearch,
    isSuccess: deleteSavedSearchSuccess,
  } = deleteSavedSearchResult;

  const handleDelete = async () => {
    await deleteSavedSearch(context.id).then(() => {
      hideModal();
    });
  };

  useEffect(() => {
    if (deleteSavedSearchError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DeleteSavedSearchModal.deleteError',
          defaultMessage: 'Delete saved search failed. Please try again.',
          description: 'Error message when deleting saved search fails',
        }),
      );
    }
  }, [deleteSavedSearchError, intl]);

  useEffect(() => {
    if (deleteSavedSearchSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'DeleteSavedSearchModal.deleteSuccess',
            defaultMessage: '{name} search has been successfully deleted.',
            description:
              'Success message when saved search is deleted. Example: name = "Contract Search"',
          },
          { name: context?.name },
        ),
      );
    }
  }, [deleteSavedSearchSuccess, intl, context?.name]);

  return (
    <Modal
      title={intl.formatMessage({
        id: 'DeleteSavedSearchModal.modalTitle',
        defaultMessage: 'Delete Saved Search?',
        description: 'Modal title for deleting saved search',
      })}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeleteSavedSearchModal.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        }),
        variant: 'danger',
        onClick: handleDelete,
        isLoading: isDeletingSavedSearch,
      }}
      isVisible={modalType === MODAL_DELETE_SAVED_SEARCH}
      onCancel={hideModal}
      onHide={hideModal}
    >
      <FlexLayout flexDirection="column" space={4}>
        <Text variant="xs-dense">
          {intl.formatMessage(
            {
              id: 'DeleteSavedSearchModal.confirmMessage',
              defaultMessage:
                'Are you sure you want to delete "{name}" query? You won\'t be able to undo this action.',
              description:
                'Confirmation message for deleting saved search. Example: name = "Contract Search"',
            },
            { name: context?.name },
          )}
        </Text>
      </FlexLayout>
    </Modal>
  );
};

export default DeleteSavedSearchModal;
